import { useEffect, useState, useRef } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import FAQHero from './components/FAQHero';
import FAQCategorySection from './components/FAQCategorySection';
import FAQSidebar from './components/FAQSidebar';
import FAQCTA from './components/FAQCTA';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';
import { faqCategories, faqItems } from '../../mocks/faq';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function FAQPage() {
  const [activeSection, setActiveSection] = useState('moving');
  const mainRef = useRef<HTMLDivElement>(null);
  const categories = faqCategories.filter((c) => c.id !== 'all');

  // SEO with integrated FAQ schema
  useSEO({
    title: 'FAQ - Roseville California | Moving, Living & Business Listing Questions',
    description: 'Find answers to frequently asked questions about Roseville, California. Learn about moving, housing, schools, recreation, city services, and how to list your business on Roseville.org.',
    keywords: 'Roseville FAQ, Roseville questions, moving to Roseville, Roseville housing, Roseville schools, Roseville business listing, Roseville city services',
    canonical: '/faq',
    ogImage: 'https://public.readdy.ai/ai/img_res/b8fbc42ecc2793f4c9f9e424809256be.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'FAQ', url: '/faq' },
        ]),
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topEntry = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          const id = topEntry.target.id.replace('faq-', '');
          setActiveSection(id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 }
    );

    categories.forEach((cat) => {
      const el = document.getElementById(`faq-${cat.id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const getItemsByCategory = (catId: string) => {
    return faqItems.filter((item) => item.category === catId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <FAQHero />

      {/* Quick Stats */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center gap-10 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                <i className="ri-question-line text-base"></i>
              </div>
              <span><strong className="text-gray-900">{faqItems.length}</strong> Questions Answered</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <i className="ri-folder-line text-base"></i>
              </div>
              <span><strong className="text-gray-900">{categories.length}</strong> Categories</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                <i className="ri-time-line text-base"></i>
              </div>
              <span>Updated <strong className="text-gray-900">June 2025</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16" ref={mainRef}>
        <div className="flex gap-12">
          {/* Sidebar */}
          <div className="hidden lg:block w-72 shrink-0">
            <FAQSidebar activeSection={activeSection} />
          </div>

          {/* FAQ Content */}
          <div className="flex-1 min-w-0 space-y-16" itemScope itemType="https://schema.org/FAQPage">
            {categories.map((cat) => {
              const items = getItemsByCategory(cat.id);
              if (items.length === 0) return null;
              return (
                <FAQCategorySection
                  key={cat.id}
                  category={cat}
                  items={items}
                />
              );
            })}
          </div>
        </div>
      </div>

      <FAQCTA />
      <MailchimpNewsletter />
      <Footer />
    </div>
  );
}